import type {ReactNode} from 'react'
import {useCallback} from 'react'

import useLocalStorageValue from '../../hooks/useLocalStorageValue'
import useWindowWidth from '../../hooks/useWindowWidth'

import {
  getMaxSidebarWidth,
  getSidebarWidth,
  MAX_SIDEBAR_WIDTH,
  MIN_SIDEBAR_WIDTH,
} from './PageWithSidebar.utils'
import Sidebar from './Sidebar/Sidebar'

import styles from './PageWithSidebar.css'

type Props = {
  className?: string
  content: ReactNode
  sidebar?: ReactNode
  minSidebarWidth?: number
  maxSidebarWidth?: number
  isOpenSidebar?: boolean
}

function PageWithSidebar({
  className,
  content,
  sidebar,
  minSidebarWidth = MIN_SIDEBAR_WIDTH,
  maxSidebarWidth = MAX_SIDEBAR_WIDTH,
  isOpenSidebar = true,
}: Props) {
  const windowWidth = useWindowWidth()

  const [sidebarResizeDeltaValue, setSidebarResizeDeltaValue] = useLocalStorageValue(
    'jb.teamcity.pipelines.settingsSidebar.resizeDelta',
  )
  const sidebarResizeDelta =
    sidebarResizeDeltaValue != null ? parseInt(sidebarResizeDeltaValue, 10) : 0

  const calculatedMaxSidebarWidth = getMaxSidebarWidth(
    windowWidth,
    minSidebarWidth,
    maxSidebarWidth,
  )
  const sidebarWidth = Math.min(
    getSidebarWidth(windowWidth, sidebarResizeDelta),
    calculatedMaxSidebarWidth,
  )

  const onSidebarResize = useCallback(
    (deltaWidth: number) => {
      const newSidebarWidth = sidebarWidth + deltaWidth
      setSidebarResizeDeltaValue((newSidebarWidth - getSidebarWidth(windowWidth, 0)).toString())
    },
    [setSidebarResizeDeltaValue, sidebarWidth, windowWidth],
  )

  return (
    <div className={className}>
      <div
        className={styles.wrapper}
        style={{paddingRight: isOpenSidebar ? `${sidebarWidth}px` : undefined}}
      >
        {content}
        <Sidebar
          maxWidth={calculatedMaxSidebarWidth}
          minWidth={minSidebarWidth}
          sidebarWidth={sidebarWidth}
          isOpen={isOpenSidebar}
          onResize={onSidebarResize}
        >
          {sidebar}
        </Sidebar>
      </div>
    </div>
  )
}

export default PageWithSidebar
